import { action, observable, runInAction, makeObservable } from 'mobx';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { IkeRestDepartment } from '@ikegps/core-shared';

const FormImportPoleforemanModalCSS = {
	form: 'c-FormImportPoleforemanModal__form',
	spinner: 'c-FormImportPoleforemanModal__spinner',
};

addStyles({
	[FormImportPoleforemanModalCSS.form]: {
		display: 'flex',
		'flex-direction': 'column',
		gap: '10px',
		width: '400px',
		height: '100%',
	},
	[FormImportPoleforemanModalCSS.spinner]: {
		display: 'flex',
		'flex-direction': 'column',
		gap: '10px',
		'justify-content': 'center',
		'text-align': 'center',
	},
});

type IPFDatabaseVersion = {
	organizationID: string;
	organizationName: string;
	databaseURL: string;
};

export class FormImportPoleforemanModal extends ModalFormComponent {
	departmentSelectState: SelectInputState;
	@observable loadingSecondaryForms: boolean = false;
	secondaryFormSelectState: SelectInputState;
	selectedSecondaryFormId: string | null = null;
	constructor(
		ctx: ModalFormComponentCtx,
		private existingFormVersion?: string
	) {
		super(ctx);
		makeObservable(this);

		this.displayCancelButton = false;
		this.displaySubmitButton = false;
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.refreshDatabases();

		this.departmentSelectState = new SelectInputState('Department');
		this.departmentSelectState.onValue(() => this.loadSecondaryForms());

		for (const dept of Services.Department.departments) {
			this.departmentSelectState.addOption({ key: dept.name, value: dept.objectId });
		}

		this.secondaryFormSelectState = new SelectInputState('Form');
		this.secondaryFormSelectState.disabled = true;
		this.secondaryFormSelectState.onValue((formID) => {
			this.selectedSecondaryFormId = formID;
		});
	}

	@observable private loadingDatabases: boolean = false;
	@observable private loadedDatabases: boolean = false;
	@observable private loadingOperatingCompanies: boolean = false;
	@observable private loadedOperatingCompanies: boolean = false;
	@observable private attachmentId: string | null = null;
	@observable private operatingCompany: string | null = null;

	@observable private databaseURL: string | null = null;

	@observable private databases: IPFDatabaseVersion[] = [];
	@observable private operatingCompanies: { id: number; name: string }[] = [];

	@observable errorMessage: T | null = null;

	private async loadSecondaryForms() {
		if (!this.departmentSelectState.value) {
			return;
		}

		runInAction(() => {
			this.loadingSecondaryForms = true;
		});

		const deptRest = new IkeRestDepartment(Services.User.rest, this.departmentSelectState.value);

		const forms = await deptRest.formSummaries.fetch();

		this.secondaryFormSelectState.reset();
		this.secondaryFormSelectState.setOptions([]);

		for (const form of forms) {
			this.secondaryFormSelectState.addOption({ key: form.displayname, value: form.revision });
		}

		runInAction(() => {
			this.loadingSecondaryForms = false;
		});
	}

	protected override customButtons(): MithrilVNode[] {
		return [
			m(
				'button',
				{ className: 'mdl-button', onclick: () => this.close(), disabled: this.loading, type: 'button' },
				Translate(T.CANCEL)
			),
			m(
				'button',
				{
					className: 'mdl-button mdl-button--accent',
					disabled: this.errorMessage || this.loading || !this.databaseURL,
					type: 'button',
					onclick: () => {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						this.refreshOperatingCompanies();
					},
				},
				Translate(T.NEXT)
			),
		];
	}
	get nodes() {
		if (this.errorMessage) {
			return m('div', {}, [Translate(this.errorMessage)]);
		}

		if (this.loadingDatabases) {
			return m('div', { className: FormImportPoleforemanModalCSS.spinner }, [
				m('div', Translate(T.FORM_IMPORT_POLEFOREMAN_FETCHING_ORGS)),
				Spinner.view(),
			]);
		}

		if (this.loadingOperatingCompanies) {
			return m('div', { className: FormImportPoleforemanModalCSS.spinner }, [
				m('div', Translate(T.FORM_IMPORT_POLEFOREMAN_FETCHING_OP_CO)),
				Spinner.view(),
			]);
		}

		// If we have databases loaded, but haven't selected the operating company yet, then display the database org selector
		if (this.loadedDatabases && !this.loadedOperatingCompanies) {
			return m.fragment({}, [
				m(
					'form',
					{ className: FormImportPoleforemanModalCSS.form },
					this.databases.length > 0
						? [
								m('div', [Translate(T.FORM_IMPORT_POLEFOREMAN_DESCRIPTION)]),
								m(
									'select#importIpfSelectOrg',
									{
										onchange: (e) => {
											if ('target' in e && e.target && 'value' in e.target && typeof e.target.value === 'string') {
												this.databaseURL = e.target.value;
												this.isSubmitButtonDisabled = false;
											}
										},
									},
									[
										m(
											'option',
											{ value: '', selected: true, disabled: true },
											Translate(T.FORM_IMPORT_POLEFOREMAN_SELECT_ORG)
										),
										...this.databases.map((db) => m('option', { value: db.databaseURL }, db.organizationName)),
									]
								),
								m('div', [Translate(T.FORM_NEW_COPY_CUSTOM_FIELDS_FROM)]),
								SelectInputComponent.view(this.departmentSelectState),
								this.loadingSecondaryForms ? Spinner.view() : SelectInputComponent.view(this.secondaryFormSelectState),
							]
						: m('div', [Translate(T.FORM_IMPORT_POLEFOREMAN_NO_DATABASES)])
				),
			]);
		}

		if (this.loadedOperatingCompanies) {
			if (this.operatingCompanies.length === 1) {
				return m('div', { className: FormImportPoleforemanModalCSS.spinner }, [
					m('div', [Translate(T.FORM_IMPORT_POLEFOREMAN_SINGLE_OP_CO, this.operatingCompanies[0]!.name)]),
					Spinner.view(),
				]);
			}
			return m(
				'form',
				{ className: FormImportPoleforemanModalCSS.form },
				this.operatingCompanies.length > 0
					? [
							m('div', [Translate(T.FORM_IMPORT_POLEFOREMAN_OP_CO_DESCRIPTION)]),
							m(
								'select',
								{
									onchange: (e) => {
										if ('target' in e && e.target && 'value' in e.target && typeof e.target.value === 'string') {
											this.operatingCompany = e.target.value;
											this.isSubmitButtonDisabled = false;
										}
									},
									disabled: this.loading,
								},
								[
									m(
										'option',
										{ value: '', selected: true, disabled: true },
										Translate(T.FORM_IMPORT_POLEFOREMAN_SELECT_OP_CO)
									),
									...this.operatingCompanies.map((op) => m('option', { value: op.id }, op.name)),
								]
							),
						]
					: m('div', [Translate(T.FORM_IMPORT_POLEFOREMAN_NO_OPERATING_COMPANIES)])
			);
		}

		return Spinner.view();
	}

	@action async importPoleForemanForm() {
		try {
			this.setLoading(true);
			const rest = Services.Department.rest;
			await rest.Form.Pla.importPoleforeman({
				attachmentId: this.attachmentId!,
				...(this.operatingCompany && { operatingCompanyId: parseInt(this.operatingCompany) }),
				...(this.existingFormVersion && { existingFormVersion: this.existingFormVersion }),
				...(this.selectedSecondaryFormId && { secondFormRevision: this.selectedSecondaryFormId }),
			});
			this.close();
			AppStore.FormSummary.refresh();
		} catch (_e) {
			this.handleError(T.FORM_IMPORT_POLEFOREMAN_ERROR_CREATION);
		}
	}

	override async submit() {
		await this.importPoleForemanForm();
	}

	@action private async refreshOperatingCompanies() {
		try {
			this.displayCustomButtons = false;
			this.loadingOperatingCompanies = true;

			const result = await Services.Department.rest.Form.Pla.savePoleForemanDb({
				...(this.databaseURL && { databaseURL: this.databaseURL }),
			});

			this.attachmentId = result.attachmentId;

			this.operatingCompanies = result.operatingCompanies.map((op) => ({
				id: op.id,
				name: op.name,
			}));

			if (this.operatingCompanies.length === 1) {
				this.operatingCompany = this.operatingCompanies[0]!.id.toString();
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.importPoleForemanForm();
			} else {
				this.isSubmitButtonDisabled = true;
			}
		} catch (_e) {
			this.handleError(T.FORM_IMPORT_POLEFOREMAN_ERROR_OP_CO);
		} finally {
			if (this.operatingCompanies.length !== 1) {
				this.displayCancelButton = true;
				this.displaySubmitButton = true;
			}
			this.loadingOperatingCompanies = false;
			this.loadedOperatingCompanies = true;
		}
	}

	@action private async refreshDatabases() {
		try {
			this.displayCustomButtons = true;
			this.loadingDatabases = true;
			this.isSubmitButtonDisabled = true;

			if (!Services.SSO.user?.sub) {
				throw new Error('User not logged in');
			}

			const result = await Services.User.rest.User.ipfDatabases({ userId: Services.SSO.user.sub });

			this.databases = result.databases;
			this.loadedDatabases = true;

			this.isSubmitButtonDisabled = true;

			if (this.databases.length === 1) {
				this.databaseURL = this.databases[0]!.databaseURL;
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.refreshOperatingCompanies();
			}
		} catch (_e) {
			this.handleError(T.FORM_IMPORT_POLEFOREMAN_ERROR_DATABASE);
		} finally {
			this.loadingDatabases = false;
		}
	}

	@action private handleError(msg: T) {
		this.setLoading(false);
		this.errorMessage = msg;
		this.displayCustomButtons = true;
	}
}
